/**
 * Access token for app-api (httpClientInvestigate).
 *
 * app-api requires a valid Auth0 access token on every request. App registers
 * useAuth0().getAccessTokenSilently here once Auth0 has loaded; the request interceptor asks it
 * for a token on every request. The SDK keeps tokens in memory and renews them itself, so this is
 * cheap and never sends an expired token.
 *
 * Requests issued before App registers the getter (child effects run before App's, and Auth0 may
 * still be loading) wait for it, up to TOKEN_GETTER_TIMEOUT_MS. When there is no token (nobody
 * signed in, Auth0 error, timeout), the request goes out without one and app-api answers 401:
 * no retry, no loop.
 */
export const TOKEN_GETTER_TIMEOUT_MS = 10000;

let tokenGetter = null;
let isRegistered = false;
let markRegistered;
const registration = new Promise((resolve) => {
  markRegistered = resolve;
});

/**
 * @param {(() => Promise<string>) | null} getter getAccessTokenSilently when signed in, else null.
 */
export function setInvestigateTokenGetter(getter) {
  tokenGetter = typeof getter === 'function' ? getter : null;
  isRegistered = true;
  markRegistered();
}

function waitForRegistration() {
  if (isRegistered) return Promise.resolve();
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(resolve, TOKEN_GETTER_TIMEOUT_MS);
  });
  return Promise.race([registration, timeout]).then(() => clearTimeout(timer));
}

export async function getInvestigateToken() {
  await waitForRegistration();
  if (!tokenGetter) return null;
  try {
    return (await tokenGetter()) || null;
  } catch (error) {
    console.warn(
      'app-api: could not get an access token, sending the request without one:',
      (error && (error.error || error.message)) || error
    );
    return null;
  }
}

/** axios request interceptor: the only source of the Authorization header for app-api. */
export async function attachInvestigateToken(config) {
  const token = await getInvestigateToken();
  const headers = config.headers || {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    delete headers.Authorization;
  }
  config.headers = headers;
  return config;
}
